import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { GitPullRequest, Save, Users, Clock, Target, TrendingUp, Activity, CheckCircle, XCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CONSULTANT_STATUS_COLORS = {
  available: 'bg-green-100 text-green-800 border-green-300',
  on_call: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  in_meeting: 'bg-blue-100 text-blue-800 border-blue-300',
  busy: 'bg-orange-100 text-orange-800 border-orange-300',
  offline: 'bg-gray-100 text-gray-800 border-gray-300',
};

const LeadPullSettings = () => {
  const { getAuthHeader, admin } = useAuth();
  const [consultantStatuses, setConsultantStatuses] = useState([]);
  const [pullEligibility, setPullEligibility] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const [formData, setFormData] = useState({
    min_hours_since_assignment: 24,
    min_hours_since_last_contact: 48,
    allow_pull_if_busy: true,
    allow_pull_if_in_meeting: true,
    allow_pull_if_on_call: false,
    min_lead_score: 50,
    max_contact_attempts: 0
  });

  useEffect(() => {
    fetchConsultantStatuses();
    fetchPullEligibility();
  }, []);

  const fetchConsultantStatuses = async () => {
    try {
      const response = await axios.get(`${API}/admin/consultants/status`, {
        headers: getAuthHeader()
      });
      setConsultantStatuses(response.data);
    } catch (error) {
      console.error('Error fetching consultant statuses:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPullEligibility = async () => {
    try {
      const response = await axios.get(`${API}/admin/pull-eligibility`, {
        headers: getAuthHeader()
      });
      setPullEligibility(response.data);
      setFormData({
        min_hours_since_assignment: response.data.min_hours_since_assignment,
        min_hours_since_last_contact: response.data.min_hours_since_last_contact,
        allow_pull_if_busy: response.data.allow_pull_if_busy,
        allow_pull_if_in_meeting: response.data.allow_pull_if_in_meeting,
        allow_pull_if_on_call: response.data.allow_pull_if_on_call,
        min_lead_score: response.data.min_lead_score,
        max_contact_attempts: response.data.max_contact_attempts
      });
    } catch (error) {
      console.error('Error fetching pull eligibility:', error);
    }
  };

  const handleSaveSettings = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/admin/pull-eligibility`, formData, {
        headers: getAuthHeader()
      });
      fetchPullEligibility();
      alert('Pull eligibility settings updated successfully!');
    } catch (error) {
      console.error('Error updating settings:', error);
      alert('Failed to update settings');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'available': return <CheckCircle size={16} />;
      case 'busy': case 'in_meeting': case 'on_call': return <Activity size={16} />;
      default: return <XCircle size={16} />;
    }
  };

  if (loading) {
    return (
      <div className="lead-pull-settings">
        <div className="scoring-header">
          <h1><GitPullRequest size={24} /> Lead Pull Settings</h1>
        </div>
        <div className="text-center py-8">Loading settings...</div>
      </div>
    );
  }

  return (
    <div className="lead-pull-settings">
      <div className="scoring-header">
        <div>
          <h1>
            <GitPullRequest size={28} /> Lead Pull System
          </h1>
          <p className="text-muted-foreground">Configure eligibility rules for consultants to pull unactioned leads</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Pull Eligibility Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target size={20} /> Pull Eligibility Rules
            </CardTitle>
            <CardDescription>Define when leads become eligible for pulling</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSaveSettings} className="space-y-4">
              <div>
                <Label>Min Hours Since Assignment</Label>
                <Input
                  type="number"
                  value={formData.min_hours_since_assignment}
                  onChange={(e) => setFormData({ ...formData, min_hours_since_assignment: parseFloat(e.target.value) })}
                  min="0"
                  step="0.5"
                />
                <p className="text-xs text-muted-foreground mt-1">How long before a lead can be pulled</p>
              </div>
              <div>
                <Label>Min Hours Since Last Contact</Label>
                <Input
                  type="number"
                  value={formData.min_hours_since_last_contact}
                  onChange={(e) => setFormData({ ...formData, min_hours_since_last_contact: parseFloat(e.target.value) })}
                  min="0"
                  step="0.5"
                />
                <p className="text-xs text-muted-foreground mt-1">No contact activity threshold</p>
              </div>
              <div>
                <Label>Minimum Lead Score</Label>
                <Input
                  type="number"
                  value={formData.min_lead_score}
                  onChange={(e) => setFormData({ ...formData, min_lead_score: parseInt(e.target.value) })}
                  min="0"
                  max="100"
                />
                <p className="text-xs text-muted-foreground mt-1">Only pull leads with score above this</p>
              </div>
              <div>
                <Label>Max Contact Attempts</Label>
                <Input
                  type="number"
                  value={formData.max_contact_attempts}
                  onChange={(e) => setFormData({ ...formData, max_contact_attempts: parseInt(e.target.value) })}
                  min="0"
                />
                <p className="text-xs text-muted-foreground mt-1">Pull if attempts are at or below this (0 = no attempts)</p>
              </div>
              
              <div className="space-y-2 pt-2 border-t">
                <Label className="text-base">Consultant Status Conditions</Label>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="allow-busy"
                      checked={formData.allow_pull_if_busy}
                      onChange={(e) => setFormData({ ...formData, allow_pull_if_busy: e.target.checked })}
                    />
                    <Label htmlFor="allow-busy" className="cursor-pointer text-sm">Allow pull if consultant is busy</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="allow-meeting"
                      checked={formData.allow_pull_if_in_meeting}
                      onChange={(e) => setFormData({ ...formData, allow_pull_if_in_meeting: e.target.checked })}
                    />
                    <Label htmlFor="allow-meeting" className="cursor-pointer text-sm">Allow pull if consultant is in meeting</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="allow-call"
                      checked={formData.allow_pull_if_on_call}
                      onChange={(e) => setFormData({ ...formData, allow_pull_if_on_call: e.target.checked })}
                    />
                    <Label htmlFor="allow-call" className="cursor-pointer text-sm">Allow pull if consultant is on call</Label>
                  </div>
                </div>
              </div>

              <Button type="submit" className="w-full">
                <Save size={16} /> Save Settings
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Current Stats */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp size={20} /> System Status
            </CardTitle>
            <CardDescription>Overview of the lead pull system</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center gap-2 mb-1">
                  <Clock size={18} className="text-blue-600" />
                  <span className="font-semibold text-blue-900">Time Thresholds</span>
                </div>
                <p className="text-sm text-blue-700">
                  Leads become pullable after {pullEligibility?.min_hours_since_assignment || 24}h of assignment
                  with {pullEligibility?.min_hours_since_last_contact || 48}h of no contact.
                </p>
              </div>
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center gap-2 mb-1">
                  <Target size={18} className="text-green-600" />
                  <span className="font-semibold text-green-900">Quality Filter</span>
                </div>
                <p className="text-sm text-green-700">
                  Only leads with score ≥ {pullEligibility?.min_lead_score || 50} are pullable.
                </p>
              </div>
              <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
                <div className="flex items-center gap-2 mb-1">
                  <Users size={18} className="text-purple-600" />
                  <span className="font-semibold text-purple-900">Active Consultants</span>
                </div>
                <p className="text-sm text-purple-700">
                  {consultantStatuses.filter(c => c.status === 'available').length} of {consultantStatuses.length} consultants available
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Consultant Status Overview */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users size={20} /> Consultant Status Overview
          </CardTitle>
          <CardDescription>Real-time status of all consultants</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {consultantStatuses.length === 0 ? (
              <p className="text-muted-foreground col-span-full text-center py-8">No consultant statuses available</p>
            ) : (
              consultantStatuses.map((consultant) => (
                <div key={consultant.username} className="p-4 border rounded-lg hover:shadow-md transition-shadow">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-purple-600 flex items-center justify-center text-white font-bold">
                        {consultant.username.substring(0, 2).toUpperCase()}
                      </div>
                      <div>
                        <h4 className="font-semibold">{consultant.username}</h4>
                      </div>
                    </div>
                  </div>
                  <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border-2 text-sm font-semibold ${CONSULTANT_STATUS_COLORS[consultant.status] || CONSULTANT_STATUS_COLORS.offline}`}>
                    {getStatusIcon(consultant.status)}
                    {consultant.status.replace('_', ' ').toUpperCase()}
                  </div>
                  {consultant.status_reason && (
                    <p className="text-xs text-muted-foreground mt-2">{consultant.status_reason}</p>
                  )}
                  <p className="text-xs text-muted-foreground mt-2">
                    Updated: {new Date(consultant.status_changed_at).toLocaleString()}
                  </p>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default LeadPullSettings;
